import { useState } from "react";
import { CheckCircle2, LayoutGrid, Loader2, PenLine, Sparkles } from "lucide-react";
import { OpCallout } from "@/components/op/OpCallout";
import { OpNowBar } from "@/components/op/OpNowBar";
import { OpRoleBadge } from "@/components/op/OpRoleBadge";
import { OpSection } from "@/components/op/OpSection";
import { OpStepRail, type OpStepItem } from "@/components/op/OpStepRail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OP_RESULT_CARD, OP_SURFACE } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

const STEPS_IDLE: OpStepItem[] = [
  { id: "concepts", label: "Koncepcje", state: "done", percent: 100 },
  { id: "copy", label: "Copy", state: "waiting", percent: 0 },
  { id: "review", label: "Recenzja", state: "waiting" },
  { id: "render", label: "Render", state: "blocked" },
];

const STEPS_RUN: OpStepItem[] = [
  { id: "concepts", label: "Koncepcje", state: "done", percent: 100 },
  { id: "copy", label: "Copy", state: "active", percent: 48, detail: "Role → slajdy" },
  { id: "review", label: "Recenzja", state: "waiting" },
  { id: "render", label: "Render", state: "blocked" },
];

const CONCEPTS = [
  { tag: "signals", text: "Przed wizytą u matki przebierasz się trzy razy.", selected: true },
  { tag: "vs-echo", text: "Może powinnaś trochę schudnąć, mówię to z troski.", selected: false },
  { tag: "dialogue", text: "Naprawdę zjesz jeszcze dokładkę przy tej wadze?", selected: false },
  { tag: "swap", text: "Od razu się tłumaczysz, gdy matka patrzy na brzuch.", selected: false },
];

const SLIDES = [
  { n: 1, role: "hasło", text: "Przed wizytą przebierasz się trzy razy." },
  { n: 2, role: "opis", text: "To nie moda. To lęk, że matka znów skomentuje ciało." },
  { n: 3, role: "opis", text: "Słyszysz echo zanim wejdziesz do domu." },
  { n: 4, role: "close", text: "Możesz wejść w swoim ubraniu." },
];

export function OpPrzetwarzanieDemo() {
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(0);

  return (
    <div className="space-y-4">
      <OpCallout variant="help" title="Makieta Przetwarzanie (CopyTab)">
        Lewa: koncepcje + copy + CTA. Prawa: Co z czego / transkrypt / recenzja. Tła z nowego kontrastu OVL.
      </OpCallout>

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant={running ? "brand" : "outline"} onClick={() => setRunning((v) => !v)}>
          {running ? "Job: ON" : "Job: off"}
        </Button>
      </div>

      <OpNowBar
        label={running ? "Czekaj: generuję copy (Role → Slajdy)" : "Wybierz koncepcję, potem Generuj treść"}
        detail="Makieta bez API. Status jak StudioProcessChain."
      />

      <div className="grid gap-4 xl:grid-cols-12">
        <div className="space-y-4 xl:col-span-7">
          <OpSection role="now" step={1} title="Koncepcje" hint="Wybierz jedną kartę (tor AI).">
            <div className={cn("space-y-3 p-3", OP_SURFACE.result.box)}>
              <OpRoleBadge tone="result" label="Wynik · koncepcje" />
              <div className="grid gap-2 sm:grid-cols-2">
                {CONCEPTS.map((c, i) => (
                  <button
                    key={c.tag}
                    type="button"
                    onClick={() => setSelected(i)}
                    className={cn("p-3", selected === i ? OP_RESULT_CARD.selected : OP_RESULT_CARD.idle)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5">
                        {selected !== i ? <LayoutGrid className="h-3.5 w-3.5 text-indigo-700" aria-hidden /> : null}
                        <Badge
                          variant={selected === i ? "success" : "outline"}
                          className={selected !== i ? "border-indigo-600 text-indigo-900" : undefined}
                        >
                          {c.tag}
                        </Badge>
                      </span>
                      {selected === i ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                          Wybrane
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase text-indigo-700">Wynik</span>
                      )}
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-900">{c.text}</p>
                  </button>
                ))}
              </div>
            </div>
          </OpSection>

          <div className={cn("p-4", OP_SURFACE.params.box)}>
            <OpRoleBadge tone="params" />
            <div className="mt-3 flex flex-wrap gap-2">
              {["mieszanka", "lista", "dialog"].map((t, i) => (
                <span
                  key={t}
                  className={cn(
                    "rounded-md border px-2.5 py-1 text-xs font-medium",
                    i === 0 ? "border-blue-700 bg-blue-200 text-blue-950" : "border-blue-300 bg-white/70 text-blue-900",
                  )}
                >
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs font-semibold text-blue-950">Szablon wizualny</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <span className="rounded-md border border-blue-700 bg-blue-200 px-2.5 py-1 text-xs font-medium">
                Bez paczki
              </span>
              <span className="rounded-md border border-blue-300 bg-white/70 px-2.5 py-1 text-xs">Krem Kasi</span>
            </div>
            <label className="mt-3 block text-xs font-semibold text-blue-950">Twoja uwaga do modelu</label>
            <textarea
              className="mt-1 min-h-[72px] w-full rounded-md border-2 border-blue-400 bg-white p-2 text-sm"
              placeholder="np. mniej o diecie, więcej o wstydzie w szafie"
              defaultValue=""
            />
          </div>

          <OpSection
            role="path"
            step={2}
            title="Copy slajdów"
            hint="Po generacji: hasło + opis per slajd."
            actions={
              <Button type="button" variant="brand" size="lg" disabled={running}>
                {running ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generuj treść · w toku
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generuj treść
                  </>
                )}
              </Button>
            }
          >
            {running ? (
              <div className={cn("p-3", OP_SURFACE.process.box)}>
                <OpRoleBadge tone="process" label="Proces · copy" className="mb-2" />
                <p className="flex items-center gap-2 text-sm font-semibold text-cyan-950">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  2/3 Slajdy · odbiór treści
                </p>
                <div className="mt-2 flex h-3 gap-1">
                  <div className="flex-1 rounded-sm bg-violet-200">
                    <div className="h-full w-full rounded-sm bg-violet-500" />
                  </div>
                  <div className="flex-1 rounded-sm bg-sky-200">
                    <div className="h-full w-1/2 animate-pulse rounded-sm bg-sky-500" />
                  </div>
                  <div className="flex-1 rounded-sm bg-orange-200" />
                </div>
                <div className="mt-1 flex gap-1 text-[10px] text-cyan-900/80">
                  <span className="flex-1">Role</span>
                  <span className="flex-1 font-semibold text-cyan-950">Slajdy</span>
                  <span className="flex-1">Recenzja</span>
                </div>
              </div>
            ) : (
              <div className={cn("space-y-2 p-3", OP_SURFACE.result.box)}>
                <OpRoleBadge tone="result" className="mb-1" />
                {SLIDES.map((s) => (
                  <div key={s.n} className={cn("p-3", OP_RESULT_CARD.static)}>
                    <div className="mb-1 flex items-center gap-2">
                      <LayoutGrid className="h-3.5 w-3.5 text-indigo-700" aria-hidden />
                      <Badge variant="outline" className="border-indigo-600 text-indigo-900">
                        Slajd {s.n}
                      </Badge>
                      <span className="text-[10px] font-bold uppercase text-indigo-800">{s.role}</span>
                    </div>
                    <p className="text-sm text-indigo-950">{s.text}</p>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button type="button" variant="outline">
                    <PenLine className="mr-2 h-3.5 w-3.5" />
                    Popraw z moją uwagą
                  </Button>
                  <Button type="button" variant="brand">
                    Złóż obrazki
                  </Button>
                </div>
              </div>
            )}
          </OpSection>

          <details className={cn("p-3", OP_SURFACE.optional.box)}>
            <summary className="cursor-pointer text-sm font-semibold text-violet-950">
              Opcjonalne · Odczyt slajdów (inspiracja)
            </summary>
            <p className="mt-2 text-xs text-violet-900">Wrzuć zrzut · Przeanalizuj · Przebuduj koncepcje</p>
          </details>
        </div>

        <div className="space-y-3 xl:col-span-5">
          <OpStepRail
            className="xl:sticky xl:top-4"
            title="Proces"
            now={running ? "Generuję copy…" : "Koncepcja wybrana · czekam na Generuj"}
            steps={running ? STEPS_RUN : STEPS_IDLE}
          />

          <div className={cn("p-3", OP_SURFACE.help.box)}>
            <OpRoleBadge tone="help" label="Co z czego" />
            <ul className="mt-2 space-y-1 text-sm text-yellow-950">
              <li>Karta: {CONCEPTS[selected]?.tag}</li>
              <li>Slajdy: {running ? "…" : "4"}</li>
              <li>Recenzja: {running ? "czeka" : "pass"}</li>
            </ul>
          </div>

          <div className={cn("p-3", OP_SURFACE.review.box)}>
            <div className="flex items-center justify-between gap-2">
              <OpRoleBadge tone="review" />
              <Badge variant="warning">rewrite</Badge>
            </div>
            <p className="mt-2 text-sm text-orange-950">Hak słaby na slajdzie 1. Close nie domyka wstyd.</p>
            <ul className="mt-2 space-y-1.5 text-sm">
              {["Wzmocnij hak (konkret ciała)", "Close: pozwolenie, nie rada"].map((row) => (
                <li key={row} className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1 accent-success" defaultChecked />
                  <span className="text-orange-950">{row}</span>
                </li>
              ))}
            </ul>
            <Button type="button" variant="brand" size="sm" className="mt-3">
              Wdróż zaznaczone
            </Button>
          </div>

          <div className={cn("p-3 text-[11px] text-zinc-100", OP_SURFACE.log.box)}>
            <OpRoleBadge tone="log" label="Transkrypt / log" className="mb-2" />
            <p className="text-zinc-300">15:48:01 · role_start</p>
            <p className="text-zinc-100">15:48:12 · copy_stream · 2.1k zn</p>
            <p className="text-zinc-300">15:48:40 · review_pending</p>
          </div>
        </div>
      </div>
    </div>
  );
}
